<template>
	<div>
		<button
			v-if="!$store.workspace.canInviteFullMember"
			class="popover-link gap-6 flex px-10 items-center mt-4"
			data-test="manage-members-button"
			@click="$routerHelpers.navigateToWorkspaceSettings('members')"
		>
			<BaseIcon name="mono/settings" class="w-16 h-16" /> <span>Manage members</span>
		</button>
		<button
			v-else-if="showAddToTeamButton"
			class="popover-link gap-6 flex px-10 items-center mt-4"
			data-test="invite-to-team-button"
			@click="openForm"
		>
			<BaseIcon name="mono/plus" class="w-16 h-16" /> <span>Invite team member</span>
		</button>

		<form v-else-if="showForm" data-test="invite-to-team-form" @submit.prevent @invalid.prevent.capture>
			<div class="px-12 flex gap-8">
				<BaseIcon name="mono/email" class="mt-10" :class="{'text-red-500': error}" />
				<div class="flex-grow">
					<input
						ref="emailInput"
						v-model.trim="email"
						data-test="email-input"
						type="email"
						placeholder="name@example.com"
						pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
						class="w-full block bg-transparent text-gray-100 pb-2"
						style="height: 36px"
						autofocus
						autocomplete
					/>
					<p v-if="error" data-test="error-message" class="text-red-500 text-sm -mt-8 mb-8">
						{{ error.message }}
					</p>
				</div>
			</div>
			<BaseButton
				type="submit"
				class="button-blue w-full h-32 -mb-4 justify-center"
				style="border-top-right-radius: 0; border-top-left-radius: 0"
				data-test="submit-button"
				:loading="isSubmitting"
				@click="onSubmit"
			>
				{{ isSubmitting ? 'Sending invite' : 'Send invite' }}
			</BaseButton>
		</form>
	</div>
</template>

<script>
import analytics from 'shared/utils/analytics'
import {useWorkspaceInvites} from 'shared/composables/useWorkspaceInvites'

export default {
	name: 'InviteMemberInPopover',
	props: {
		formOpen: {type: Boolean, default: false},
		location: {type: String, default: ''},
	},
	emits: ['close'],
	setup() {
		const {defaultRole, existingInvitedEmails, existingMembersEmails, sendInvites} = useWorkspaceInvites()
		return {
			defaultRole,
			sendInvites,
			existingMembersEmails,
			existingInvitedEmails,
		}
	},
	data() {
		return {
			showAddToTeamButton: true,
			showForm: false,
			isSubmitting: false,
			email: '',
			error: null,
		}
	},
	watch: {
		email() {
			this.error = null
		},
		formOpen: {
			immediate: true,
			handler(formOpen) {
				if (formOpen) {
					this.openForm()
				}
			},
		},
	},
	unmounted() {
		document.removeEventListener('keydown', this.onKeyDown)
	},
	methods: {
		openForm() {
			if (this.showForm) return
			this.showForm = true
			this.showAddToTeamButton = false
			document.addEventListener('keydown', this.onKeyDown)
			this.$nextTick(() => {
				this.$refs.emailInput.focus()
			})
		},
		cancel() {
			this.email = ''
			this.showForm = false
			this.showAddToTeamButton = true
			document.removeEventListener('keydown', this.onKeyDown)
		},
		onKeyDown(e) {
			e.stopPropagation()
			if (e.key === 'Escape') {
				this.cancel()
			}
		},
		validateForm() {
			const {validity, value} = this.$refs.emailInput
			const inputValue = value.trim()

			if (!inputValue) return

			// use the Constraint validation API to check for valid email addresses
			// https://developer.mozilla.org/en-US/docs/Web/API/Constraint_validation#element.setcustomvalidity(message)
			if (!validity.valid && (validity.typeMismatch || validity.patternMismatch)) {
				throw new Error('Enter a valid email', {cause: 'validationError'})
			}

			if (this.existingMembersEmails.includes(inputValue)) {
				// check if email is already a member
				throw new Error('Already on the team', {cause: 'validationError'})
			}
			if (this.existingInvitedEmails.includes(inputValue)) {
				// check if email is already invited
				throw new Error('Already invited', {cause: 'validationError'})
			}
		},
		async onSubmit() {
			if (this.isSubmitting) return
			if (!this.email) return

			this.isSubmitting = true

			try {
				this.validateForm()
			} catch (error) {
				// validation errors are shown in the form UI
				this.showForm = true
				await this.$nextTick()
				this.$refs.emailInput?.focus()
				this.error = error
				this.isSubmitting = false
				return
			}

			try {
				await this.sendInvites([{email: this.email, role: this.defaultRole}])
				this.showForm = false
				this.showAddToTeamButton = true
				this.email = ''
				this.$emit('close')
				analytics.track('Invite Form Submitted', {
					invites_count: 1,
					from: this.location,
				})
			} catch (error) {
				const browserstackEmailErrors = error.response?.browserstackEmailErrors
				if (!browserstackEmailErrors) throw error
				// browserstack email errors (validation errors) are shown in the form UI
				this.showForm = true
				await this.$nextTick()
				this.$refs.emailInput?.focus()
				this.error = {message: this.extractBrowserstackErrorMessage(browserstackEmailErrors)}
				return
			} finally {
				this.isSubmitting = false
			}
		},
		extractBrowserstackErrorMessage(browserstackEmailErrors) {
			const emailError = browserstackEmailErrors.find(
				error => error.email === this.email && error.hasError && error.errorMsg
			)
			if (!emailError) return 'There was an error.'
			return emailError.errorMsg
		},
	},
}
</script>
